import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { PageFrame } from '@/components/PageFrame'
import { Button } from '@/components/ui/button'
import { SegmentedControl } from '@/components/ui/controls'
import { Panel, Skeleton } from '@/components/ui/display'
import {
  extractNote,
  loadExternalShare,
  setNoteBody,
  setNoteShared,
  shareExternally,
  type ExternalShare,
  type ShareRecipient,
  type ShareResult,
} from '@/data/externalShare'
import { DEFAULT_LANGUAGE, LANGUAGES, currentPerson } from '@/data/people'
import type { MeetingNote, Person } from '@/data/types'
import { RecipientList } from './RecipientList'
import { SharedNoteList } from './SharedNoteList'

/**
 * The page the recap email's share button opens.
 *
 * It is OUTSIDE the app shell — no sidebar, no command palette, no chat. The
 * reader arrived from their inbox to answer one question about one meeting, and
 * the shell would offer them everything except the thing they came for.
 *
 * The shape is the two decisions, in the order they get made — who it reaches,
 * then what reaches them — with a bar underneath that says what pressing the
 * button will do. Both were on the old page; what has changed is that the
 * consequence is COUNTED rather than described ("the summary, the shared notes"
 * said the same words whether four notes were going out or none), the two
 * halves are panels in one column instead of two cards with their own
 * headings, and every state — loading, expired, sent — keeps the same frame, so
 * confirming a share does not land you on a page that looks like a different
 * product.
 */
export function ExternalSharePage({ token }: { token: string }) {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => ['external-share', token], [token])

  const { data: share, isPending, error } = useQuery({
    queryKey,
    queryFn: () => loadExternalShare(token),
    retry: false,
  })

  /**
   * Addresses typed in on this page, held here rather than written into the
   * cache: nothing has been granted until Share is pressed, and the cache is
   * what the server said.
   */
  const [added, setAdded] = useState<ShareRecipient[]>([])
  const [selection, setSelection] = useState<Set<string> | null>(null)
  const [language, setLanguage] = useState<string>(DEFAULT_LANGUAGE)
  const [busy, setBusy] = useState(false)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<ShareResult | null>(null)

  const recipients = useMemo(
    () => [...(share?.recipients ?? []), ...added],
    [share?.recipients, added]
  )

  /**
   * Everyone who does not already have access starts selected, so the common
   * case — "send it to the people who were on the call" — stays one click.
   */
  const initial = useMemo(
    () =>
      new Set(
        (share?.recipients ?? [])
          .filter((person) => !person.alreadyShared)
          .map((person) => person.email)
      ),
    [share?.recipients]
  )
  const selected = selection ?? initial

  const notes = share?.notes ?? []
  const sharedNotes = notes.filter((note) => note.sharedExternally).length

  /** Notes are patched into the cache rather than refetched — the store returns them. */
  const patchNotes = useCallback(
    (next: MeetingNote[]) => {
      queryClient.setQueryData<ExternalShare>(queryKey, (old) => old && { ...old, notes: next })
    },
    [queryClient, queryKey]
  )

  const run = useCallback(
    (action: Promise<MeetingNote[]>) => {
      setBusy(true)
      void action
        .then(patchNotes)
        .catch(() => toast.error('That change could not be saved.'))
        .finally(() => setBusy(false))
    },
    [patchNotes]
  )

  const toggle = (email: string) => {
    const next = new Set(selected)
    if (next.has(email)) next.delete(email)
    else next.add(email)
    setSelection(next)
  }

  const addRecipient = (person: Person) => {
    setAdded((current) => [
      ...current,
      { ...person, onInvite: false, alreadyShared: false },
    ])
    setSelection(new Set(selected).add(person.email))
  }

  const removeRecipient = (email: string) => {
    setAdded((current) => current.filter((person) => person.email !== email))
    const next = new Set(selected)
    next.delete(email)
    setSelection(next)
  }

  const send = async () => {
    if (!share || selected.size === 0) return
    setSending(true)
    try {
      setResult(await shareExternally(share, Array.from(selected), language))
    } catch {
      toast.error('Sharing is unavailable right now.')
    } finally {
      setSending(false)
    }
  }

  /* ----- What the frame says in each state ----- */

  const heading = (title: React.ReactNode) => (
    <>
      <span className="block text-2xs font-medium tracking-wide text-[var(--color-fg-subtle)] uppercase">
        Share externally
      </span>
      {title}
    </>
  )

  let body: React.ReactNode
  // No meeting, no meeting title: a placeholder in the h1 is a claim about
  // which meeting failed, and on a dead token there is not one to name.
  let title: React.ReactNode = 'Share externally'
  let subtitle: React.ReactNode
  let footer: React.ReactNode

  if (isPending) {
    title = heading(<Skeleton className="mt-1 h-5 w-64" />)
    body = (
      <div className="space-y-3">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  } else if (error) {
    body = (
      <Outcome
        tone="danger"
        title="Nothing was shared"
        detail={error instanceof Error ? error.message : 'This sharing link is no longer valid.'}
        note="You can still share this meeting from the app."
      />
    )
  } else if (!share.enabled) {
    body = (
      <Outcome
        tone="danger"
        title="Sharing from email is turned off"
        detail="An administrator has disabled sharing from recap emails for your organisation."
      />
    )
  } else if (result) {
    title = heading(share.title)
    subtitle = share.startsAt ? when(share.startsAt) : null
    body = <Sent result={result} notes={sharedNotes} />
  } else {
    title = heading(share.title)
    subtitle = share.startsAt ? when(share.startsAt) : null
    body = (
      <div className="space-y-3">
        <RecipientList
          token={share.token}
          recipients={recipients}
          selected={selected}
          busy={busy || sending}
          onToggle={toggle}
          onAdd={addRecipient}
          onRemove={removeRecipient}
        />
        <SharedNoteList
          notes={notes}
          busy={busy || sending}
          onToggleShared={(noteId, shared) => run(setNoteShared(share, noteId, shared))}
          onEdit={(noteId, text) => run(setNoteBody(share, noteId, text))}
          onExtract={(templateId) => run(extractNote(share, templateId))}
        />
      </div>
    )
    footer = (
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* A minimum width rather than `min-w-0`: left free to shrink, the
            sentence squeezes into a five-line column beside the button on a
            phone instead of taking a row of its own. */}
        <p className="min-w-48 flex-1 text-2xs text-[var(--color-fg-subtle)]">
          {consequence(selected.size, sharedNotes)}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          {/* Beside the button, not in a settings panel: the language is only
              ever decided at the moment of sending, and it is the one property
              of the email the sender still controls here. */}
          <SegmentedControl
            size="sm"
            value={language}
            onValueChange={setLanguage}
            options={LANGUAGES.map((item) => ({
              value: item.code,
              label: item.code.toUpperCase(),
              title: item.label,
            }))}
          />
          <Button
            variant="primary"
            loading={sending}
            disabled={selected.size === 0 || busy}
            onClick={() => void send()}
          >
            {selected.size === 0
              ? 'Nobody selected'
              : `Share with ${selected.size} ${selected.size === 1 ? 'person' : 'people'}`}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-[var(--color-canvas)]">
      <header className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4">
        <span className="text-xs font-semibold tracking-tight text-[var(--color-fg)]">
          Nohup Notes
        </span>
        {/* Which account is about to send. The link arrives by email and can be
            forwarded, so whose name goes on it is worth stating. */}
        <span className="truncate text-2xs text-[var(--color-fg-subtle)]">
          Sharing as {currentPerson().name}
        </span>
      </header>

      <div className="min-h-0 flex-1">
        <PageFrame width="narrow" title={title} subtitle={subtitle} footer={footer}>
          {body}
        </PageFrame>
      </div>
    </div>
  )
}

function when(startsAt: string): string {
  return new Date(startsAt).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Exactly what the button is about to do, counted rather than described. */
function consequence(people: number, notes: number): string {
  if (people === 0) return 'Nobody is selected, so nothing will be sent.'
  const who = people === 1 ? 'One person gets' : `${people} people each get`
  const what =
    notes === 0
      ? 'an email with the summary and a link to the meeting — no notes are included'
      : `an email with the summary, ${notes === 1 ? 'the one shared note' : `the ${notes} shared notes`}, and a link to the meeting`
  return `${who} ${what}.`
}

/**
 * Every ending, in one shape.
 *
 * Sent, expired and turned-off are the same kind of statement — this is what
 * happened, and it is over — so they are the same panel with a different mark
 * on top. Three differently-built outcome screens is how a confirmation ends up
 * looking like it belongs to another product than the page that produced it.
 */
function Outcome({
  tone,
  title,
  detail,
  note,
  children,
}: {
  tone: 'success' | 'danger'
  title: string
  detail?: string
  note?: string
  children?: React.ReactNode
}) {
  const Icon = tone === 'success' ? CheckCircle2 : AlertTriangle
  return (
    <Panel>
      <div className="flex flex-col items-center gap-2 px-6 py-8 text-center">
        <Icon
          className={
            tone === 'success'
              ? 'size-4 text-[var(--color-success)]'
              : 'size-4 text-[var(--color-danger)]'
          }
        />
        <p className="text-xs font-medium text-[var(--color-fg)]">{title}</p>
        {detail ? (
          <p className="max-w-sm text-2xs text-[var(--color-fg-subtle)]">{detail}</p>
        ) : null}
        {note ? (
          <p className="max-w-sm text-2xs text-[var(--color-fg-subtle)]">{note}</p>
        ) : null}
      </div>
      {children}
    </Panel>
  )
}

function Sent({ result, notes }: { result: ShareResult; notes: number }) {
  return (
    <Outcome
      tone="success"
      title={`Shared with ${result.shared.length} ${result.shared.length === 1 ? 'person' : 'people'}`}
      detail={
        result.shared.length === 0
          ? 'Nothing was sent.'
          : `Each of them now has an email with the summary, ${
              notes === 1 ? 'the shared note' : `the ${notes} shared notes`
            }, and a link to the meeting.`
      }
    >
      {result.shared.length > 0 ? (
        <ul className="divide-y divide-[var(--color-border-subtle)] border-t border-[var(--color-border)]">
          {result.shared.map((email) => (
            <li key={email} className="truncate px-3 py-1.5 text-2xs text-[var(--color-fg-muted)]">
              {email}
            </li>
          ))}
        </ul>
      ) : null}

      {result.failed.length > 0 ? (
        <p className="border-t border-[var(--color-border)] px-3 py-2 text-2xs text-[var(--color-danger)]">
          Could not reach {result.failed.join(', ')}.
        </p>
      ) : null}
    </Outcome>
  )
}
